import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import { Package, User } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { trackTelegramClick } from "../utils/telegramTracking";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const isCustomerLoggedIn = user && user.role === 'customer';

  return (
    <>
    {/* Top Bar - Social & Contact (desktop) */}
    <div className="bg-[#0a0a0f] text-gray-300 text-xs py-1.5 px-4 hidden lg:block">
      <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-between items-center">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-gray-500">📞 +39 347 080 8245</span>
          <span className="text-gray-600">|</span>
          <span className="text-gray-500">📍 Corso Europa 175, Villaricca (NA)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-gray-500 mr-1">Trovaci su</span>
          <a href="https://wa.me/393470808245" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 font-semibold transition-colors">WHATSAPP</a>
          <a href="https://facebook.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">FACEBOOK</a>
          <a href="https://instagram.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 font-semibold transition-colors">INSTAGRAM</a>
          <a href="https://t.me/edicolavillaricca" target="_blank" rel="noopener noreferrer" onClick={() => trackTelegramClick("top_bar")} data-testid="nav-telegram-link" className="text-[#229ED9] hover:text-white font-semibold transition-colors">TELEGRAM</a>
          <a href="https://tiktok.com/@edicolavillaricca" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white font-semibold transition-colors">TIKTOK</a>
          <a href="https://ebay.it/str/edicolacerasuolo" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 font-semibold transition-colors">EBAY</a>
          <a href="https://it.wallapop.com/app/catalog/published" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors">WALLAPOP</a>
          <span className="text-gray-600 ml-1">|</span>
          <LanguageSwitcher testId="nav-desktop-language" />
        </div>
      </div>
    </div>
    {/* Top Bar - Social (mobile, compact) */}
    <div className="bg-[#0a0a0f] text-xs py-1.5 px-4 lg:hidden border-b border-white/5">
      <div className="flex flex-wrap justify-center items-center gap-3" data-testid="mobile-social-bar">
        <a href="https://wa.me/393470808245" target="_blank" rel="noopener noreferrer" className="text-green-400 font-semibold">WHATSAPP</a>
        <a href="https://facebook.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="text-blue-400 font-semibold">FACEBOOK</a>
        <a href="https://instagram.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="text-pink-400 font-semibold">INSTAGRAM</a>
        <a href="https://ebay.it/str/edicolacerasuolo" target="_blank" rel="noopener noreferrer" className="text-yellow-400 font-semibold">EBAY</a>
      </div>
    </div>
    <nav className="bg-[#0a0a0f] border-b border-white/10 shadow-lg sticky top-0 z-50" aria-label="Navigazione principale">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex gap-2 justify-between items-center min-h-14 py-2">
          {/* Logo — compact pill */}
          <div className="flex items-center min-w-0">
            <a
              href="/"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              aria-label="Torna alla homepage di Edicola Cerasuolo"
              data-testid="nav-logo"
            >
              <Package className="w-4 h-4 text-amber-400" aria-hidden="true" />
              <span className="text-sm font-semibold text-white min-w-0">Edicola Cerasuolo</span>
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6">
            <a href="/" className="text-gray-300 hover:text-amber-400 font-medium text-sm">{t('nav.home')}</a>
            <a href="/prodotti" className="text-gray-300 hover:text-amber-400 font-medium text-sm">{t('nav.products')}</a>
            <a href="/chi-siamo" className="text-gray-300 hover:text-amber-400 font-medium text-sm">{t('nav.about')}</a>
            <a href="/contatto" className="text-gray-300 hover:text-amber-400 font-medium text-sm">{t('nav.contact')}</a>
            {isCustomerLoggedIn ? (
              <a
                href="/account"
                data-testid="nav-account-link"
                className="text-gray-300 hover:text-amber-400 font-medium flex items-center gap-1 text-sm"
              >
                <User className="w-4 h-4" />
                {t('nav.account') || 'Il mio account'}
              </a>
            ) : (
              <a
                href="/login"
                data-testid="nav-login-link"
                className="text-gray-300 hover:text-amber-400 font-medium flex items-center gap-1 text-sm"
              >
                <User className="w-4 h-4" />
                {t('nav.login') || 'Accedi'}
              </a>
            )}
            <button 
              onClick={() => window.open(`${window.location.origin}/gestione`, '_blank', 'noopener,noreferrer')}
              className="text-gray-300 hover:text-amber-400 font-medium bg-transparent border-none cursor-pointer text-sm"
              aria-label="Apri gestione in una nuova finestra"
            >
              {t('nav.management')}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2 shrink-0">
            <LanguageSwitcher testId="nav-mobile-language" />
            <button
              data-testid="nav-mobile-menu-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-amber-400 focus:outline-none"
              aria-label={isOpen ? "Chiudi menu di navigazione" : "Apri menu di navigazione"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#0a0a0f] border-t border-white/10">
              <a href="/" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">{t('nav.home')}</a>
              <a href="/prodotti" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">{t('nav.products')}</a>
              <a href="/chi-siamo" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">{t('nav.about')}</a>
              <a href="/contatto" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">{t('nav.contact')}</a>
              {isCustomerLoggedIn ? (
                <a href="/account" data-testid="nav-account-link-mobile" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">
                  {t('nav.account') || 'Il mio account'}
                </a>
              ) : (
                <a href="/login" data-testid="nav-login-link-mobile" className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium">
                  {t('nav.login') || 'Accedi'}
                </a>
              )}
              <button 
                onClick={() => window.open(`${window.location.origin}/gestione`, '_blank', 'noopener,noreferrer')}
                className="block px-3 py-2 text-gray-300 hover:text-amber-400 font-medium bg-transparent border-none cursor-pointer text-left w-full"
                aria-label="Apri gestione in una nuova finestra"
              >
                {t('nav.management')}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};
